import { useEffect, useState, useCallback } from 'preact/hooks'
import { lookupsApi } from '@/api'
import type { EmailTypeOption } from '@/types/lookups'

const cache = new Map<string, EmailTypeOption[]>()

export interface UseEmailTypesLookupResult {
  data: EmailTypeOption[] | null
  isLoading: boolean
  error: string | null
  refetch: () => void
}

function cacheKey(workspaceId: string, webId: string): string {
  return `${workspaceId}::${webId}`
}

/**
 * Loads the web's email-type catalog (ORB-363) for the ad-hoc "create email copy" picker. Returns an
 * empty array for web types without a catalog (Meeting/Demo) — the picker then offers Custom only.
 */
export function useEmailTypesLookup(
  workspaceId: string | undefined,
  webId: string | undefined
): UseEmailTypesLookupResult {
  const key = workspaceId && webId ? cacheKey(workspaceId, webId) : null
  const [data, setData] = useState<EmailTypeOption[] | null>(key ? cache.get(key) ?? null : null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchTypes = useCallback(async (force = false) => {
    if (!workspaceId || !webId) {
      setData(null)
      return
    }
    const k = cacheKey(workspaceId, webId)
    if (!force && cache.has(k)) {
      setData(cache.get(k) ?? null)
      return
    }

    setIsLoading(true)
    setError(null)
    const response = await lookupsApi.getEmailTypes(workspaceId, webId)
    if (response.error || !response.data) {
      setError(response.error || 'Failed to load email types')
    } else {
      cache.set(k, response.data)
      setData(response.data)
    }
    setIsLoading(false)
  }, [workspaceId, webId])

  useEffect(() => {
    void fetchTypes()
  }, [fetchTypes])

  // Lets callers refresh after server-side changes (e.g. after generating a copy, so AlreadyGenerated
  // updates on the next open). Clears the cached entry for this key and forces a fresh fetch.
  const refetch = useCallback(() => {
    if (key) cache.delete(key)
    void fetchTypes(true)
  }, [key, fetchTypes])

  return { data, isLoading, error, refetch }
}
